import ScriptLoader from './Utilities/script-loader';
import config from '../config';
import { injectable } from 'inversify';

const CANDU_CLIENT_TOKEN = config.canduClientToken;
const CANDU_SCRIPT_URL = `https://cdn.candu.ai/sdk/latest/candu.umd.js?token=${CANDU_CLIENT_TOKEN}`;

export enum CANDU_EVENTS {
    /** Onboarding events */
    ONBOARDING_COMPLETED = 'Onboarding completed',
}

interface CanduEventing {
    identify: (userId: string, traits?: object) => void;
    track: (eventName: string, properties?: object) => void;
    page: (pageName: string, properties?: object) => Promise<void>;
    screen: (screenName: string, properties?: object) => void;
    group: (groupId: string, traits?: object) => void;
}

declare global {
    interface Window {
        Candu?: {
            init: (config: any) => void;
            renderContent: (config: any) => void;
            getEventing: () => CanduEventing;
        };
    }
}

@injectable()
class CanduService {
    private _isInitialized = false;
    private _scriptLoaded = false;
    private _isdevMode = config.env === 'dev';
    private _eventing?: CanduEventing;

    private async _loadScript(): Promise<void> {
        if (this._scriptLoaded) {
            return;
        }
        await ScriptLoader.loadScript(CANDU_SCRIPT_URL);
        this._scriptLoaded = true;
    }

    public async initializeCandu(userIdentity: { logged_in_user_id: number; portal_id: number; portal_name: string }): Promise<void> {
        if (!this._isdevMode) {
            console.log('[CanduService::init] Candu is disabled in non-development environment');
            return;
        }

        if (!CANDU_CLIENT_TOKEN) {
            console.error('[CanduService::init] Candu client token not found');
            return;
        }
        
        if (this._isInitialized) {
            return;
        }

        try {

            const distinctId = `${userIdentity.logged_in_user_id}|${userIdentity.portal_id}|${userIdentity.portal_name}`;
            await this._loadScript();
            
            if (window.Candu) {
                window.Candu.init({
                    userId: distinctId,
                    clientToken: CANDU_CLIENT_TOKEN,
                });
                this._isInitialized = true;
                this._eventing = window.Candu.getEventing();
                console.log('[CanduService::initializeCandu] Candu initialized with distinct ID:', distinctId);
            } else {
                console.warn('[CanduService::initializeCandu] Candu initialization failed: Candu SDK not available after script load');
            }
        } catch (error) {
            console.error('[CanduService::initializeCandu] failed:', error);
        }
    }

    public renderContent(slug: string, selector: string): void {
        if (window.Candu) {
            window.Candu.renderContent({
                slug,
                selector
            });
        }
    }

    public trackCanduEvent(eventName: CANDU_EVENTS): void {
        try {
            /** we initialize candu only for dev evn, so this._eventing is undefined for prod */
            if (!this._eventing) {
                console.warn('Candu eventing not initialized. env: ', config.env);
                return;
            }
            this._eventing.track(eventName);

        } catch (error) {
            console.error('Candu track event failed:', error);
        }
    }
}

export default CanduService; 